import * as React from 'react'

import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox'
import { PlusIcon } from 'lucide-react'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { Loader } from '~/client/src/shared/components/Loader'
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '~/client/src/shared/components/shadcn-ui/combobox'
import StruxhubTextValueSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubTextValueSelector'
import InitialState from '~/client/src/shared/stores/InitialState'

import { MAX_LEVELS_PER_BUILDING } from './levelIndexes'

// translated

const MAX_VALUE = MAX_LEVELS_PER_BUILDING
const MIN_VALUE = 0
const MAX_OPTION_SELECT_VALUE = 30

interface IProps {
  state?: InitialState
  label: string
  value: number
  /** The highest count it offers; MAX_LEVELS_PER_BUILDING when unset. */
  max?: number
  onChange: (amount: number) => void
}

@inject('state')
@observer
export default class LevelCombobox extends React.Component<IProps> {
  @observable public customOption: number = 0
  @observable public value: number = null
  private readonly options = [
    ...new Array(MAX_OPTION_SELECT_VALUE).fill(0),
  ].map((n, idx) => ({
    label: idx,
    value: idx,
  }))

  public constructor(props: IProps) {
    super(props)
    this.value = props.value
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    runInAction(() => {
      if (this.props.value !== prevProps.value) {
        this.forceUpdate()
        this.customOption = this.props.value
      }
      if (this.value !== this.props.value) this.value = this.props.value
    })
  }

  public componentDidMount() {
    runInAction(() => {
      this.customOption = this.props.value
    })
  }

  public render() {
    const { state, value, label } = this.props

    return (
      <Combobox
        items={this.visibleOptions}
        filter={null}
        value={value}
        onValueChange={this.handleChange}
        inputValue={this.customOption.toString()}
        onInputValueChange={this.onOptionChange}
        onOpenChangeComplete={this.handleOpenChangeComplete}
        disabled={state.isLoading}
      >
        <ComboboxPrimitive.Trigger
          nativeButton={false}
          render={<div className="tw:w-full" />}
        >
          <StruxhubTextValueSelector
            label={label}
            value={value.toString()}
            isRequiredTextHidden={true}
          />
        </ComboboxPrimitive.Trigger>
        <ComboboxContent side="top" className="tw:w-30 tw:min-w-30 tw:max-w-30">
          {state.isLoading ? (
            <Loader />
          ) : (
            <>
              <ComboboxInput
                type="number"
                min={MIN_VALUE}
                max={this.max}
                showTrigger={false}
              />
              <ComboboxList>
                {(option: number) => (
                  <ComboboxItem key={option} value={option}>
                    {this.isCustomOption && <PlusIcon />}
                    <span className="tw:min-w-0 tw:flex-1 tw:truncate">
                      {option}
                    </span>
                  </ComboboxItem>
                )}
              </ComboboxList>
            </>
          )}
        </ComboboxContent>
      </Combobox>
    )
  }

  private handleChange = (optionValue: number | null) => {
    if (optionValue !== null) {
      this.props.onChange(optionValue)
    }
  }

  private onOptionChange = (inputValue: string) => {
    runInAction(() => {
      this.customOption = Number(inputValue)
    })
  }

  private handleOpenChangeComplete = (open: boolean) => {
    if (!open) {
      runInAction(() => {
        this.customOption = this.props.value
      })
    }
  }

  private get isCustomOption(): boolean {
    return this.customOption >= MAX_OPTION_SELECT_VALUE
  }

  private get max(): number {
    return this.props.max ?? MAX_VALUE
  }

  private get visibleOptions(): number[] {
    if (this.isCustomOption) {
      return this.customOption <= this.max ? [this.customOption] : []
    }

    return this.sortedOptions
      .map(option => option.value)
      .filter(value => value <= this.max)
  }

  @computed
  private get sortedOptions() {
    if (this.customOption === this.value) {
      return this.options
    }

    return this.options
      .filter(({ label }) =>
        label.toString().includes(this.customOption.toString()),
      )
      .sort((o1, o2) => o1.label - o2.label)
  }
}
